import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Upload, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import { EXPENSE_CATEGORY_OPTIONS, formatOmr } from "./constants";
import {
  listRiderExpenses, getRiderExpenseKpis, createRiderExpense,
  updateRiderExpense, deleteRiderExpense, exportRiderExpenses,
} from "../../services/payroll.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

/**
 * KPI tiles keyed on the backend's stable expenseNature values, so renaming an
 * expense type in Settings cannot empty a tile. Fuel Balance is derived
 * server-side as top-up minus used.
 */
const KPI_CARDS = [
  { key: "deduction", label: "Deductions" },
  { key: "benefit", label: "Benefits" },
  { key: "codDeduction", label: "COD Deductions" },
  { key: "advanceCodDeduction", label: "Advance COD Deductions" },
  { key: "fuelTopup", label: "Fuel Top-up" },
  { key: "fuelUsed", label: "Fuel Used" },
  { key: "fuelBalance", label: "Fuel Balance", allowNegative: true },
  { key: "partnerSpend", label: "Partner Spend" },
];

export default function RiderExpensesTab() {
  const { canEdit, canDelete } = usePermission("payrollExpenses");
  const { options: expenseTypeOptions } = useSettingsDropdown("expense-emi-types", {
    filter: (item) => item.expenseCategory !== "company",
  });
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: fleetOptions } = useSettingsDropdown("fleet-vehicle-types", {
    filter: (item) => item.category === "fleet",
  });
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState();
  const [expenseType, setExpenseType] = useState();
  const [project, setProject] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      expenseCategory: category || undefined,
      expenseType: expenseType || undefined,
      project: project || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, category, expenseType, project, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listRiderExpenses(params({ page, per_page: pageSize })),
        getRiderExpenseKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load rider expenses");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [search, category, expenseType, project, dateRange]);

  const openModal = (record = null) => {
    setAttachment(null);
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        rider: record.rider?._id,
        startDate: record.startDate ? dayjs(record.startDate) : null,
        expenseCategory: record.expenseCategory,
        expenseType: record.expenseType?._id,
        amount: record.amount,
        project: record.project?._id,
        fleetType: record.fleetType?._id,
        description: record.description,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ expenseCategory: "expense", startDate: dayjs() });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        startDate: values.startDate.format("YYYY-MM-DD"),
        ...(attachment ? { attachment } : {}),
      };
      if (modal.record) {
        await updateRiderExpense(modal.record._id, payload);
        message.success("Expense updated");
      } else {
        await createRiderExpense(payload);
        message.success("Expense added");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save expense");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Expense",
      content: `Delete the ${record.expenseTypeName} expense for ${record.partnerName}?`,
      onConfirm: async () => {
        try {
          await deleteRiderExpense(record._id);
          message.success("Expense deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete expense");
        }
      },
    });
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Start Date", dataIndex: "startDateLabel" },
    { title: "Project", dataIndex: "projectName" },
    { title: "Fleet Type", dataIndex: "fleetTypeName" },
    { title: "Expense Type", dataIndex: "expenseTypeName" },
    { title: "Category", dataIndex: "expenseCategoryLabel" },
    {
      title: "Amount (OMR)",
      dataIndex: "amount",
      align: "right",
      render: formatOmr,
    },
    { title: "Description", dataIndex: "description", ellipsis: true },
  ];

  return (
    <div>
      <KPIGrid>
        {KPI_CARDS.map((card) => (
          <KPICard
            key={card.key}
            label={card.label}
            value={kpis[card.key]}
            unit="OMR"
            allowNegative={card.allowNegative}
          />
        ))}
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search partner or description"
          allowClear
          style={{ width: 260 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Category"
          allowClear
          style={{ width: 200 }}
          options={EXPENSE_CATEGORY_OPTIONS}
          value={category}
          onChange={setCategory}
        />
        <Select
          placeholder="Expense Type"
          allowClear
          style={{ width: 200 }}
          options={expenseTypeOptions}
          value={expenseType}
          onChange={setExpenseType}
        />
        <Select
          placeholder="Project"
          allowClear
          style={{ width: 160 }}
          options={projectOptions}
          value={project}
          onChange={setProject}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Rider_Expenses"
          onExport={() => exportRiderExpenses(params())}
        />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Add Expense
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Expense" : "Add Expense"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={620}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={onRiderSearch}
              loading={ridersLoading}
              options={riderOptions}
              placeholder="Search by partner ID or name"
            />
          </Form.Item>
          <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="expenseCategory" label="Expense Category" rules={[{ required: true }]}>
            <Select options={EXPENSE_CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item name="expenseType" label="Expense Type" rules={[{ required: true }]}>
            <Select options={expenseTypeOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="amount" label="Amount (OMR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item name="project" label="Project">
            <Select options={projectOptions} allowClear />
          </Form.Item>
          <Form.Item name="fleetType" label="Fleet Type">
            <Select options={fleetOptions} allowClear />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          <Form.Item label="Attachment">
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setAttachment(file);
                return false;
              }}
              onRemove={() => setAttachment(null)}
              fileList={attachment ? [attachment] : []}
            >
              <Button icon={<UploadOutlined />}>Select file (max 2 MB)</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
